import { motion } from "framer-motion";
import { Instagram, Facebook, Youtube, MessageCircle, MapPin, ArrowUpRight } from "lucide-react";
import { useLang } from "./LanguageContext";
import { Reveal } from "./Reveal";
import { TRX_URL, WA_LINK, WA_DISPLAY, SOCIALS, LOGO_URL } from "./content";

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, youtube: Youtube };

export const Footer = () => {
  const { t } = useLang();

  return (
    <footer id="contact" className="relative border-t border-white/10 overflow-hidden">
      {/* CTA band */}
      <div className="relative py-24 md:py-32">
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.04]">
          <span className="font-display font-black text-[28vw] tracking-tighter text-white leading-none whitespace-nowrap">
            NEXCIP
          </span>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="max-w-4xl">
              <h2 className="font-display font-black uppercase text-4xl md:text-7xl tracking-tighter leading-[0.95] text-white">
                {t.footer.cta}
              </h2>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <a
                  href={TRX_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-register-btn"
                  className="group inline-flex items-center justify-center gap-2 h-14 px-8 bg-[#ccff00] text-black font-bold text-sm hover:bg-[#b3e600] transition-colors"
                >
                  {t.cta.register}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-btn"
                  className="inline-flex items-center justify-center gap-2 h-14 px-8 border border-white/20 text-white font-medium text-sm hover:bg-white/5 hover:border-[#ccff00] transition-colors"
                >
                  <MessageCircle className="h-4 w-4 text-[#ccff00]" />
                  {t.footer.waLabel}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Info grid */}
      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="NEXCIP" className="h-10 w-10 object-cover rounded-sm" />
              <div className="leading-none">
                <span className="font-display font-black text-xl text-white">NEXCIP</span>
                <span className="block text-[10px] tracking-[0.25em] text-zinc-500 uppercase mt-1">
                  Lima Cipta Digital
                </span>
              </div>
            </div>
            <p className="mt-5 text-zinc-400 font-light text-sm max-w-xs leading-relaxed">{t.footer.tagline}</p>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-xs font-bold tracking-[0.25em] uppercase text-zinc-500 mb-4">
              {t.footer.contact}
            </h4>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-wa-link"
              className="group flex items-center gap-3 text-white hover:text-[#ccff00] transition-colors"
            >
              <MessageCircle className="h-5 w-5" />
              <span className="font-medium">{WA_DISPLAY}</span>
            </a>
            <div className="mt-6">
              <h4 className="text-xs font-bold tracking-[0.25em] uppercase text-zinc-500 mb-3">
                {t.footer.address}
              </h4>
              <p className="flex items-start gap-3 text-zinc-400 font-light text-sm max-w-xs leading-relaxed">
                <MapPin className="h-5 w-5 shrink-0 text-[#ccff00]" />
                {t.footer.addressValue}
              </p>
            </div>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-xs font-bold tracking-[0.25em] uppercase text-zinc-500 mb-4">
              {t.footer.follow}
            </h4>
            <div className="flex flex-col gap-3">
              {SOCIALS.map((s) => {
                const Icon = SOCIAL_ICONS[s.key];
                return (
                  <a
                    key={s.key}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`social-${s.key}`}
                    className="group flex items-center gap-3 text-zinc-300 hover:text-white transition-colors"
                  >
                    <span className="flex h-10 w-10 items-center justify-center border border-white/15 group-hover:border-[#ccff00] group-hover:text-[#ccff00] transition-colors">
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="leading-tight">
                      <span className="block text-sm font-medium">{s.label}</span>
                      <span className="block text-xs text-zinc-500">{s.handle}</span>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <span className="text-xs text-zinc-600">
              © {new Date().getFullYear()} NEXCIP · Lima Cipta Digital. {t.footer.rights}
            </span>
            <a
              href="https://topup.limaciptadigital.co.id/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors"
            >
              topup.limaciptadigital.co.id
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const WhatsAppFloat = () => {
  const { t } = useLang();
  return (
    <motion.a
      href={WA_LINK}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ scale: 1.06 }}
      className="fixed bottom-6 right-6 z-50 flex items-center gap-3 h-14 pl-4 pr-5 bg-[#ccff00] text-black font-bold text-sm shadow-[0_0_40px_-8px_rgba(204,255,0,0.6)]"
      aria-label={t.footer.waLabel}
    >
      <MessageCircle className="h-6 w-6" />
      <span className="hidden sm:inline">WhatsApp</span>
    </motion.a>
  );
};
